import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  QrCodeIcon, 
  MessageCircle, 
  Shield, 
  Users,
  ArrowRight,
  CheckCircle,
  MessageSquare,
  Star,
  Play
} from 'lucide-react';
import { api } from '../services/api';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { useAuth } from '../context/AuthContext';
import DemoModal from '../components/DemoModal';
import SignInModal from '../components/SignInModal';

export default function LandingPage() {
  const [stats, setStats] = useState({ total_users: 0, total_messages: 0, total_qr_codes: 0, active_codes: 0 });
  const [showDemo, setShowDemo] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'yearly'
  const { user } = useAuth();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleQRScan = async () => {
    try {
      // Create modal for scanning
      const modal = document.createElement('div');
      modal.id = 'qr-scanner-modal';
      modal.className = 'fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 backdrop-blur-sm';
      modal.innerHTML = `
        <div class="bg-white p-6 rounded-xl max-w-md w-full mx-4 shadow-2xl">
          <div class="text-center mb-4">
            <h3 class="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Scan QR Code</h3>
            <p class="text-gray-600 text-sm mt-2">Point your camera at the QR code or enter manually below</p>
          </div>
          <div id="qr-reader" style="width: 100%; border-radius: 12px; overflow: hidden;"></div>
          <div id="camera-error" class="hidden mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"></div>
          <div class="mt-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Or enter code/URL manually:</label>
            <input 
              type="text" 
              placeholder="Paste QR code or URL here" 
              class="w-full p-3 border-2 border-gray-200 rounded-lg mb-3 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all" 
              id="manual-code" 
            />
            <div class="flex gap-3">
              <button class="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 font-medium transform hover:scale-105 transition-all duration-200" id="go-button">
                Go
              </button>
              <button class="flex-1 bg-gray-100 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-200 font-medium transition-all duration-200" id="cancel-button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      `;

      document.body.appendChild(modal);
      
      const errorDiv = modal.querySelector('#camera-error');

      // Initialize QR Scanner - will auto-request camera
      const html5QrcodeScanner = new Html5QrcodeScanner(
        "qr-reader",
        { 
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
          rememberLastUsedCamera: true,
          showTorchButtonIfSupported: true,
          formatsToSupport: [0, 1, 2, 3],
          // Automatically request permissions
          requestPermissions: true,
          verbose: false
        },
        false
      );

      // Handle successful scan
      const onScanSuccess = (decodedText, decodedResult) => {
        console.log(`QR Code detected: ${decodedText}`);
        
        // Stop scanner
        html5QrcodeScanner.clear().catch(() => {});
        
        // Remove modal
        const modalElement = document.getElementById('qr-scanner-modal');
        if (modalElement) {
          document.body.removeChild(modalElement);
        }
        
        // Navigate to the QR code page
        let uid = decodedText.trim();
        if (uid.includes('/')) {
          uid = uid.split('/').pop();
        }
        if (uid.includes('?')) {
          uid = uid.split('?')[0];
        }
        
        window.location.href = `/${uid}`;
      };

      // Handle scan error/failure
      const onScanError = (errorMessage) => {
        // Show user-friendly error for camera issues
        if (errorMessage.includes('NotFoundError') || errorMessage.includes('NotAllowedError')) {
          errorDiv.textContent = '📷 Camera not accessible. Please check permissions and ensure no other app is using it.';
          errorDiv.classList.remove('hidden');
        } else if (errorMessage.includes('NotReadableError')) {
          errorDiv.textContent = '⚠️ Camera in use by another application. Please close other apps.';
          errorDiv.classList.remove('hidden');
        }
        // Don't log "no QR code found" messages
        else if (!errorMessage.includes('No MultiFormat Readers') && !errorMessage.includes('NotFoundException')) {
          console.warn(`QR Scan error: ${errorMessage}`);
        }
      };

      // Start scanning immediately
      try {
        html5QrcodeScanner.render(onScanSuccess, onScanError);
      } catch (e) {
        console.error('Failed to start scanner:', e);
        errorDiv.textContent = 'Unable to start camera. Please use manual entry below.';
        errorDiv.classList.remove('hidden');
      }

      // Handle manual code entry
      const goButton = modal.querySelector('#go-button');
      const cancelButton = modal.querySelector('#cancel-button');
      const manualInput = modal.querySelector('#manual-code');

      // Allow Enter key to submit
      manualInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          goButton.click();
        }
      });

      goButton.onclick = async () => {
        const code = manualInput.value.trim();
        
        if (code && code.length > 0) {
          try {
            await html5QrcodeScanner.clear().catch(() => {});
          } catch (e) {
            console.log('Scanner cleanup');
          }
          
          const modalElement = document.getElementById('qr-scanner-modal');
          if (modalElement) {
            document.body.removeChild(modalElement);
          }
          
          let uid = code;
          if (code.includes('/')) {
            uid = code.split('/').pop();
          }
          if (uid.includes('?')) {
            uid = uid.split('?')[0];
          }
          
          window.location.href = `/${uid}`;
        } else {
          manualInput.focus();
          manualInput.classList.add('border-red-500', 'shake');
          setTimeout(() => {
            manualInput.classList.remove('border-red-500', 'shake');
          }, 500);
        }
      };

      cancelButton.onclick = async () => {
        try {
          await html5QrcodeScanner.clear().catch(() => {});
        } catch (e) {
          console.log('Scanner cleanup');
        }
        const modalElement = document.getElementById('qr-scanner-modal');
        if (modalElement) {
          document.body.removeChild(modalElement);
        }
      };

    } catch (error) {
      console.error('QR Scanner initialization failed:', error);
      const code = prompt('Camera not available. Please enter the QR code or URL:');
      if (code) {
        let uid = code.trim();
        if (uid.includes('/')) {
          uid = uid.split('/').pop();
        }
        if (uid.includes('?')) {
          uid = uid.split('?')[0];
        }
        window.location.href = `/${uid}`;
      }
    }
  };

  const features = [
    {
      icon: QrCodeIcon,
      title: "QR Code Generation",
      description: "Create unique QR codes for your items with custom messages and titles."
    },
    {
      icon: MessageCircle,
      title: "Anonymous Messaging", 
      description: "Enable two-way communication without revealing personal contact information."
    },
    {
      icon: Shield,
      title: "Privacy First",
      description: "All messages are routed through our secure servers. No direct contact sharing."
    },
    {
      icon: Users,
      title: "Easy to Use",
      description: "No complicated setup. Scan, claim, and start communicating immediately."
    }
  ];

  const useCases = [
    {
      title: "Lost & Found",
      description: "Perfect for wallets, bags, laptops, and other personal items.",
      icon: "🎒"
    },
    {
      title: "Customer Service",
      description: "Contact the manager systems for restaurants and retail stores.",
      icon: "🏪"
    },
    {
      title: "Property Management",
      description: "Easy communication for rental properties and maintenance requests.",
      icon: "🏠"
    },
    {
      title: "Event Networking",
      description: "Connect with attendees anonymously at conferences and events.",
      icon: "🎪"
    }
  ];

  const pricingPlans = [
    {
      name: "Free",
      monthlyPrice: 0,
      yearlyPrice: 0,
      features: [
        "10 QR codes",
        "10 messages per month",
        "Email notifications only",
        "1 notification contact per QR"
      ],
      cta: "Get Started Free",
      highlighted: false
    },
    {
      name: "Basic",
      monthlyPrice: 2,
      yearlyPrice: 20,
      savingsPercent: 17,
      features: [
        "50 QR codes",
        "100 messages per month",
        "Email & SMS notifications",
        "Up to 3 notification contacts per QR",
        "Priority support"
      ],
      cta: "Choose Basic",
      highlighted: true
    },
    {
      name: "Premium",
      monthlyPrice: 5,
      yearlyPrice: 49,
      savingsPercent: 18,
      features: [
        "200 QR codes",
        "200 messages per month",
        "Email & SMS notifications",
        "Up to 10 notification contacts per QR",
        "Premium support",
        "Advanced analytics"
      ],
      cta: "Go Premium",
      highlighted: false
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <QrCodeIcon className="h-8 w-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">iComment.app</span>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/store" data-testid="store-link">
                <Button variant="ghost">🛍️ Storefront - Get QR Message Codes</Button>
              </Link>
              {user ? (
                <Link to="/dashboard">
                  <Button data-testid="dashboard-btn">
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    onClick={() => setShowSignIn(true)}
                    data-testid="sign-in-modal-btn"
                  >
                    Sign In
                  </Button>
                  <Link to="/auth">
                    <Button data-testid="get-started-btn">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900">
        {/* Professional animated background elements */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-20 left-10 w-20 h-20 bg-blue-300 rounded-full opacity-30 animate-pulse"></div>
          <div className="absolute top-40 right-20 w-32 h-32 bg-indigo-300 rounded-full opacity-25 animate-bounce" style={{animationDuration: '3s'}}></div>
          <div className="absolute bottom-20 left-1/4 w-16 h-16 bg-cyan-300 rounded-full opacity-20 animate-ping" style={{animationDuration: '2s'}}></div>
          <div className="absolute top-1/2 left-1/2 w-40 h-40 bg-blue-400 rounded-full opacity-10 animate-pulse" style={{animationDelay: '1s'}}></div>
          {/* Professional grid overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative">
          <Badge className="mb-6 animate-fade-in bg-white/20 text-white border-white/30 text-2xl px-8 py-3" data-testid="hero-badge">
            ✨ Anonymous Communication Made Simple
          </Badge>
          
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight animate-slide-up drop-shadow-2xl" data-testid="hero-title">
            Connect Without <br />
            <span className="bg-gradient-to-r from-cyan-300 via-white to-blue-200 bg-clip-text text-transparent animate-gradient">
              Revealing Contact Info
            </span>
          </h1>
          
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto animate-fade-in-delayed drop-shadow-lg" data-testid="hero-description">
            Create QR codes for lost & found, customer service, and secure anonymous messaging. 
            <strong className="text-cyan-300"> No phone numbers or emails exposed!</strong>
          </p>
          
          {/* Animated QR Code Demo */}
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <div className="w-24 h-24 bg-gray-900 rounded-lg p-2 animate-bounce-gentle">
                <div className="w-full h-full bg-white rounded grid grid-cols-8 grid-rows-8 gap-[1px]">
                  {Array.from({length: 64}).map((_, i) => (
                    <div 
                      key={i} 
                      className={`${Math.random() > 0.5 ? 'bg-black' : 'bg-white'} animate-pulse`}
                      style={{animationDelay: `${i * 20}ms`}}
                    ></div>
                  ))}
                </div>
              </div>
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                <MessageSquare className="w-3 h-3 text-white" />
              </div>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up-delayed">
            <Link to="/auth">
              <Button size="lg" className="text-lg px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-200" data-testid="hero-cta-primary">
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Button 
              variant="outline" 
              size="lg" 
              className="text-lg px-8 py-3 hover:bg-blue-50 transition-colors duration-200 bg-white/20 border-white/30 text-white hover:bg-white hover:text-blue-600" 
              onClick={() => setShowDemo(true)}
              data-testid="hero-cta-secondary"
            >
              <Play className="mr-2 h-5 w-5" />
              Watch Demo
            </Button>
          </div>
          
          {/* QR Scanner Button */}
          <div className="mt-8 text-center animate-slide-up-delayed">
            <Button 
              size="lg" 
              className="text-lg px-8 py-3 bg-green-600 hover:bg-green-700 transform hover:scale-105 transition-all duration-200" 
              onClick={handleQRScan}
              data-testid="qr-scan-button"
            >
              <QrCodeIcon className="mr-2 h-5 w-5" />
              I Found a Message Code
            </Button>
            <p className="text-sm text-blue-100 mt-2">Scan a QR code to send an anonymous message</p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600" data-testid="stat-users">
                {stats.total_users.toLocaleString()}
              </div>
              <div className="text-gray-600">Users</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600" data-testid="stat-messages">
                {stats.total_messages.toLocaleString()}
              </div>
              <div className="text-gray-600">Messages Sent</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600" data-testid="stat-qr-codes">
                {stats.total_qr_codes.toLocaleString()}
              </div>
              <div className="text-gray-600">QR Codes Created</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-orange-600" data-testid="stat-active-codes">
                {stats.active_codes.toLocaleString()}
              </div>
              <div className="text-gray-600">Active Codes</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Condensed */}
      <section className="py-16 px-4" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-lg text-gray-600">
              Three simple steps to anonymous communication
            </p>
          </div>
          
          {/* Process Flow */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <QrCodeIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Create QR Code</h3>
              <p className="text-gray-600">Generate a unique QR code for your item with a custom message</p>
            </div>
            
            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <MessageCircle className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Someone Scans</h3>
              <p className="text-gray-600">Finder scans the code and sends you an anonymous message</p>
            </div>
            
            <div className="text-center group">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Stay Anonymous</h3>
              <p className="text-gray-600">Chat securely without revealing personal contact information</p>
            </div>
          </div>

          {/* Key Features */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border-0 bg-gradient-to-br from-white to-gray-50" data-testid={`feature-card-${index}`}>
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases - Condensed */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50 px-4" data-testid="use-cases-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Popular Use Cases
            </h2>
            <p className="text-lg text-gray-600">
              From lost items to business communication
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((useCase, index) => (
              <Card key={index} className="hover:shadow-lg hover:-translate-y-2 transition-all duration-300 bg-white/80 backdrop-blur-sm border-0" data-testid={`use-case-card-${index}`}>
                <CardContent className="p-6 text-center">
                  <div className="text-3xl mb-3">{useCase.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {useCase.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {useCase.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section - Condensed */}
      <section className="py-16 px-4 bg-gradient-to-r from-blue-900 via-purple-900 to-blue-900" data-testid="pricing-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-3">
              Simple Pricing
            </h2>
            <p className="text-lg text-blue-100 mb-6">
              Start free, upgrade when you need more
            </p>
            
            {/* Billing Cycle Toggle */}
            <div className="flex items-center justify-center space-x-3 mb-8">
              <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-white' : 'text-blue-300'}`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                  billingCycle === 'yearly' ? 'bg-green-500' : 'bg-blue-400'
                }`}
                data-testid="billing-toggle"
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                    billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-white' : 'text-blue-300'}`}>
                Yearly
              </span>
              {billingCycle === 'yearly' && (
                <span className="ml-2 px-2 py-1 bg-green-500 text-white text-xs font-semibold rounded-full animate-pulse">
                  Save up to 18%
                </span>
              )}
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {pricingPlans.map((plan, index) => (
              <Card 
                key={index} 
                className={`relative hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 ${
                  plan.highlighted 
                    ? 'border-2 border-yellow-400 bg-gradient-to-br from-white to-yellow-50 transform scale-105' 
                    : 'bg-white/95 backdrop-blur-sm'
                }`}
                data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white animate-pulse">
                      ⭐ Most Popular
                    </Badge>
                  </div>
                )}
                <CardContent className="p-6 text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  <div className="mb-4">
                    {plan.name === 'Free' ? (
                      <span className="text-3xl font-bold text-gray-900">Free</span>
                    ) : (
                      <>
                        <span className="text-3xl font-bold text-gray-900">
                          ${billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice}
                        </span>
                        <span className="text-gray-600">
                          /{billingCycle === 'monthly' ? 'month' : 'year'}
                        </span>
                        {billingCycle === 'yearly' && plan.savingsPercent && (
                          <div className="text-xs text-green-600 font-semibold mt-1">
                            Save ${(plan.monthlyPrice * 12) - plan.yearlyPrice} per year ({plan.savingsPercent}% off)
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  <ul className="space-y-2 mb-6 text-sm">
                    {plan.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center justify-center">
                        <CheckCircle className="h-4 w-4 text-green-600 mr-2 flex-shrink-0" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/pricing">
                    <Button 
                      className={`w-full transition-all duration-200 ${
                        plan.highlighted 
                          ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white' 
                          : 'variant-outline hover:bg-blue-50'
                      }`}
                      data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50 px-4" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Trusted by Thousands
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              See what our customers say about iComment.app
            </p>
            <div className="flex justify-center items-center mt-4 space-x-1">
              {[1,2,3,4,5].map((star) => (
                <Star key={star} className="w-6 h-6 text-yellow-400 fill-current" />
              ))}
              <span className="ml-2 text-gray-600 font-medium">4.9/5 from 1,247 reviews</span>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white shadow-xl hover:shadow-2xl transition-all duration-300 border-0">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  <img 
                    src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Ccircle cx='20' cy='20' r='20' fill='%234F46E5'/%3E%3Ctext x='20' y='26' text-anchor='middle' fill='white' font-family='Arial' font-size='16' font-weight='bold'%3ES%3C/text%3E%3C/svg%3E"
                    alt="Sarah M."
                    className="w-10 h-10 rounded-full mr-3"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">Sarah M.</h4>
                    <p className="text-sm text-gray-600">Small Business Owner</p>
                  </div>
                </div>
                <div className="flex mb-3">
                  {[1,2,3,4,5].map((star) => (
                    <Star key={star} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed">
                  "Perfect for our restaurant! Customers can give feedback anonymously by scanning QR codes on tables. We've improved our service significantly thanks to honest feedback."
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-xl hover:shadow-2xl transition-all duration-300 border-0">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  <img 
                    src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Ccircle cx='20' cy='20' r='20' fill='%2306B6D4'/%3E%3Ctext x='20' y='26' text-anchor='middle' fill='white' font-family='Arial' font-size='16' font-weight='bold'%3EM%3C/text%3E%3C/svg%3E"
                    alt="Michael R."
                    className="w-10 h-10 rounded-full mr-3"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">Michael R.</h4>
                    <p className="text-sm text-gray-600">College Student</p>
                  </div>
                </div>
                <div className="flex mb-3">
                  {[1,2,3,4,5].map((star) => (
                    <Star key={star} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed">
                  "Got my wallet back in 2 hours! The QR sticker worked perfectly - someone found it, scanned it, and messaged me the location. Amazing system!"
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-xl hover:shadow-2xl transition-all duration-300 border-0">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  <img 
                    src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Ccircle cx='20' cy='20' r='20' fill='%23EC4899'/%3E%3Ctext x='20' y='26' text-anchor='middle' fill='white' font-family='Arial' font-size='16' font-weight='bold'%3EL%3C/text%3E%3C/svg%3E"
                    alt="Linda K."
                    className="w-10 h-10 rounded-full mr-3"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">Linda K.</h4>
                    <p className="text-sm text-gray-600">Property Manager</p>
                  </div>
                </div>
                <div className="flex mb-3">
                  {[1,2,3,4,5].map((star) => (
                    <Star key={star} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed">
                  "We use iComment for tenant communications. QR codes on mailboxes let tenants report issues anonymously. It's revolutionized our maintenance workflow!"
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 via-indigo-700 to-blue-800 px-4" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Get Started? 🚀
          </h2>
          <p className="text-lg text-green-100 mb-6">
            Join the anonymous communication revolution
          </p>
          <Link to="/auth">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-4 transform hover:scale-105 transition-all duration-200 shadow-xl" data-testid="final-cta">
              Start Free Trial Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <QrCodeIcon className="h-6 w-6" />
                <span className="text-lg font-bold">iComment.app</span>
              </div>
              <p className="text-gray-400">
                Anonymous two-way messaging through QR codes. Connect without revealing contact information.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/features" className="hover:text-white">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
                <li><Link to="/use-cases" className="hover:text-white">Use Cases</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/help" className="hover:text-white">Help Center</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact Us</Link></li>
                <li><Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/about" className="hover:text-white">About Us</Link></li>
                <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 iComment.app. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Demo Modal */}
      <DemoModal isOpen={showDemo} onClose={() => setShowDemo(false)} />
      
      {/* Sign In Modal */}
      <SignInModal isOpen={showSignIn} onClose={() => setShowSignIn(false)} />
    </div>
  );
}